// Keeps the document title, description, canonical link and share tags in step with the page
// that is shown. Laravel renders the same tags for the first load; this covers navigation inside
// the app.
import i18n from './i18n';
import store from './store';

const DESCRIPTION_LIMIT = 160;

function meta(attribute, name) {
  let element = document.head.querySelector(`meta[${attribute}="${name}"]`);

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, name);
    document.head.appendChild(element);
  }

  return element;
}

function setMeta(attribute, name, content) {
  if (content) {
    meta(attribute, name).setAttribute('content', content);
  } else {
    const element = document.head.querySelector(`meta[${attribute}="${name}"]`);

    if (element) {
      element.parentNode.removeChild(element);
    }
  }
}

function setCanonical(url) {
  let link = document.head.querySelector('link[rel="canonical"]');

  if (!url) {
    if (link) {
      link.parentNode.removeChild(link);
    }

    return;
  }

  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }

  link.setAttribute('href', url);
}

// The same shortening as the server's description of an album.
function excerpt(text) {
  const clean = text.replace(/\s+/g, ' ').trim();

  return clean.length > DESCRIPTION_LIMIT ? `${clean.slice(0, DESCRIPTION_LIMIT).trim()}…` : clean;
}

function absolute(url) {
  return new URL(url, window.location.origin).href;
}

// The metadata of a route, from the store where the page has data of its own.
export function metadataFor(route) {
  const defaults = {
    title: i18n.t(route.meta.titleKey || 'metadata.site_name'),
    description: route.meta.descriptionKey ? i18n.t(route.meta.descriptionKey) : '',
    image: absolute('/pentagon-logo.png'),
    // The path of the route: the address bar is only updated after the navigation hooks ran.
    canonical: route.meta.canonical !== false ? route.path : '',
  };

  if (route.name === 'home') {
    const { subheadline, image } = store.getters['intro/hero'];

    return Object.assign(defaults, {
      description: subheadline || defaults.description,
      image: absolute(image.large),
    });
  }

  if (route.name === 'Album') {
    const { data } = store.getters['intro/albumPage'](route.params.slug);

    if (data) {
      // The caption is usually a date, so only the description makes a useful summary.
      const summary = data.description;

      return Object.assign(defaults, {
        title: i18n.t('metadata.album_page_title', { title: data.title }),
        description: summary ? excerpt(summary) : i18n.t('metadata.album_description', { title: data.title }),
        image: data.cover ? absolute(data.cover.thumb) : defaults.image,
      });
    }
  }

  return defaults;
}

export function applyMetadata({
  title, description, image, canonical,
}) {
  const url = canonical ? `${window.location.origin}${canonical}` : '';

  document.title = title;
  setMeta('name', 'description', description);
  setCanonical(url);
  setMeta('property', 'og:title', title);
  setMeta('property', 'og:description', description);
  setMeta('property', 'og:url', url);
  setMeta('property', 'og:image', image);
  setMeta('name', 'twitter:title', title);
  setMeta('name', 'twitter:description', description);
  setMeta('name', 'twitter:image', image);
}
